import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useGetClosedMonth, useGetSystemReferenceDate } from '@common/slices';
import { ModifyAmendmentDetails } from '@common/types';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { FormFieldType } from '@components/dynamic-form/types/inputTypes';
import { useDynamicFormDrawerStyles } from '@components/dynamic-form/style';
import Loader from '@components/loader/Loader';
import { OpenConfirmationAction } from '@core/redux/confirmation/actions';
import { IConfirmation } from '@core/redux/confirmation/types';
import { useAppSelector } from '@core/redux/hooks';
import { RootState } from '@core/redux/store';
import { useModifyCancellationAmendment } from '@features/production/amendment/amendment.slice';
import {
  AmendmentIssueDateRuleResult,
  asClosedMonthRows,
  getAmendmentEffectiveDateBounds,
  getAmendmentIssueDateRules,
  isWithinIssueDateLov
} from '@features/production/amendment/utils';

import { useFormik } from 'formik';
import * as Yup from 'yup';

type Props = {
  amendmentData: ModifyAmendmentDetails;
  onSuccess?: () => void;
};

type ModifyAmendmentDateContext = {
  issueRules: AmendmentIssueDateRuleResult;
};

type ModifyAmendmentDetailsDrawerFormProps = Props & {
  dateContext: ModifyAmendmentDateContext;
};

const ModifyAmendmentDetailsDrawerForm: React.FC<
  ModifyAmendmentDetailsDrawerFormProps
> = ({ amendmentData, onSuccess, dateContext }) => {
  const { issueRules } = dateContext;
  const buttonState = useSelector((s: RootState) => s.apiState.buttonState);
  const dispatch = useDispatch();
  const [showWarning, setShowWarning] = useState(false);

  const [modifyAmendment] = useModifyCancellationAmendment();

  const effectiveDateBounds = useMemo(
    () =>
      getAmendmentEffectiveDateBounds(
        amendmentData.policyEffectiveDate,
        amendmentData.policyExpiryDate
      ),
    [amendmentData.policyEffectiveDate, amendmentData.policyExpiryDate]
  );

  const allowAmendmentIssueDateEdit = !!useAppSelector(
    (s) => s.user?.info?.businessUser_AmendmentIssueDateEditRights
  );

  const updateAmendmentDetails = async (values: any) => {
    try {
      await modifyAmendment({
        input: {
          amendmentID: amendmentData.amendmentID,
          amendmentIssueDate: values.amendmentIssueDate,
          amendmentEffectiveDate: values.amendmentEffectiveDate,
          amendmentReason: values.cancellationReason
        }
      });
      onSuccess?.();
    } catch (error) {
      console.error('Amendment modification failed:', error);
    }
  };

  const formik = useFormik({
    initialValues: {
      cancellationReason: amendmentData.amendmentReason || '',
      amendmentEffectiveDate: amendmentData.amendmentEffectiveDate || '',
      amendmentIssueDate: amendmentData.amendmentIssueDate || ''
    },
    enableReinitialize: false,
    validationSchema: Yup.object({
      cancellationReason: Yup.string().required(
        'Cancellation Reason is required'
      ),
      amendmentEffectiveDate: effectiveDateBounds
        ? Yup.date()
            .required('Amendment Effective Date is required')
            .test(
              'effective-range',
              'Date must be within the allowed range',
              (value) =>
                !!value &&
                isWithinIssueDateLov(
                  value,
                  effectiveDateBounds.minDate,
                  effectiveDateBounds.maxDate
                )
            )
        : Yup.date().required('Amendment Effective Date is required'),
      amendmentIssueDate: Yup.date()
        .required('Amendment Issue Date is required')
        .test(
          'issue-range',
          'Date must be within the allowed range',
          (value) =>
            !allowAmendmentIssueDateEdit ||
            (!!value &&
              isWithinIssueDateLov(
                value,
                issueRules.minDate,
                issueRules.maxDate
              ))
        )
    }),
    onSubmit: async (values) => {
      const hasDateChanged =
        values.amendmentEffectiveDate !== amendmentData.amendmentEffectiveDate;

      if (hasDateChanged) {
        const confirmation: IConfirmation = {
          open: true,
          title: 'Change Amendment Effective Date',
          message:
            'Are you sure you want to change the Amendment Effective Date? If so, the system will re-calculate all variance values.',
          callback: async () => {
            await updateAmendmentDetails(values);
          },
          submitButtonText: 'Submit',
          cancelButtonText: 'Cancel'
        };
        dispatch(OpenConfirmationAction(confirmation));
      } else {
        await updateAmendmentDetails(values);
      }
    }
  });

  useEffect(() => {
    const hasDateChanged =
      formik.values.amendmentEffectiveDate !==
      amendmentData.originalEffectiveDate;
    setShowWarning(hasDateChanged);
  }, [
    formik.values.amendmentEffectiveDate,
    amendmentData.originalEffectiveDate
  ]);

  const handleChange = useCallback(
    (name: string, value: string | number | boolean) => {
      formik.setFieldTouched(name, true);
      formik.setFieldValue(name, value);
    },
    [formik]
  );

  const sections: IDynamicFormWidgetSection[] = useMemo(() => {
    const inputs: any = {
      cancellationReason: {
        name: 'cancellationReason',
        type: FormFieldType.text,
        title: 'Cancellation Reason',
        placeholder: 'Cancellation Reason',
        required: true,
        value: formik.values.cancellationReason
      },
      amendmentIssueDate: {
        name: 'amendmentIssueDate',
        type: FormFieldType.date,
        title: 'Amendment Issue Date',
        placeholder: 'Amendment Issue Date',
        required: true,
        value: formik.values.amendmentIssueDate,
        disabled: !allowAmendmentIssueDateEdit,
        minDate: issueRules.minDate,
        maxDate: issueRules.maxDate
      },
      amendmentEffectiveDate: {
        name: 'amendmentEffectiveDate',
        type: FormFieldType.date,
        title: 'Amendment Effective Date',
        placeholder: 'Amendment Effective Date',
        required: true,
        value: formik.values.amendmentEffectiveDate,
        minDate: effectiveDateBounds?.minDate,
        maxDate: effectiveDateBounds?.maxDate
      }
    };

    return [
      {
        sectionTitle: '',
        inputs
      }
    ];
  }, [
    formik.values,
    allowAmendmentIssueDateEdit,
    issueRules,
    effectiveDateBounds
  ]);

  return (
    <DynamicFormWidget
      onChange={handleChange}
      sections={sections}
      disableForm={false}
      error={formik.errors}
      onSubmit={() => formik.handleSubmit()}
      submitButtonState={buttonState}
      noteSection={
        showWarning && {
          text: 'Attention: The system will re-calculate all variance values based on the new Amendment Effective Date',
          title: ''
        }
      }
    />
  );
};

const ModifyAmendmentDetailsDrawer: React.FC<Props> = (props) => {
  const { classes } = useDynamicFormDrawerStyles();
  const { data: systemRefResp, loading: systemRefLoading } =
    useGetSystemReferenceDate();
  const { data: closedMonthResp, loading: closedMonthLoading } =
    useGetClosedMonth({ variables: { type: 'PRD' } });
  const todayRef = useRef(new Date());
  const [dateContext, setDateContext] = useState<ModifyAmendmentDateContext | null>(
    null
  );

  const isDateDataReady =
    !systemRefLoading &&
    !closedMonthLoading &&
    !!systemRefResp?.core?.getSystemReferenceDate;

  useEffect(() => {
    if (!isDateDataReady || dateContext) {
      return;
    }

    const systemReferenceDate = new Date(
      systemRefResp.core.getSystemReferenceDate
    );
    const closedMonthRows = asClosedMonthRows(
      closedMonthResp?.core?.GetClosedMonth
    );
    const issueRules = getAmendmentIssueDateRules(
      closedMonthRows,
      systemReferenceDate,
      todayRef.current
    );

    setDateContext({ issueRules });
  }, [isDateDataReady, systemRefResp, closedMonthResp, dateContext]);

  if (!dateContext) {
    return (
      <div className={classes.loaderContainer}>
        <Loader />
      </div>
    );
  }

  return (
    <ModifyAmendmentDetailsDrawerForm {...props} dateContext={dateContext} />
  );
};

export { ModifyAmendmentDetailsDrawer };
